import { Directive, Input, OnChanges } from '@angular/core';

/** The three orderYaa front-ends, each with its own brand lockup. */
export type BrandSurface = 'rider' | 'customer' | 'partner';

/**
 * Points an existing `<img>` at the brand logo for one surface, and survives the file not being there yet.
 *
 * Applied as an attribute so every layout keeps its own sizing/positioning CSS — swapping the `<img>` for a
 * component would silently drop rules that target it (`.rf__logo`, `.oy-footer__logo`, `.am__promo-logo`).
 *
 * Artwork lives in `public/images/brand/`, one file per surface plus a `-white` variant for dark backgrounds
 * (both rider and customer render on a red gradient, so they use the white one everywhere). If a file is
 * missing the image falls back to the shared `orderyaa-white.png` that shipped before per-surface logos, so
 * a half-populated brand folder degrades to the old mark instead of showing a broken image.
 */
@Directive({
  selector: 'img[appBrandLogo]',
  standalone: true,
  host: {
    '[src]': 'src',
    '[alt]': 'alt',
    '(error)': 'onError()',
  },
})
export class BrandLogoDirective implements OnChanges {
  /** Which front-end's lockup to show. */
  @Input('appBrandLogo') surface: BrandSurface = 'customer';

  /** True on a dark background (headers, footers) — picks the white variant. */
  @Input() onDark = false;

  /** Shipped before per-surface artwork existed; still the safety net for a missing file. */
  private static readonly FALLBACK = 'images/webps/orderyaa-white.png';

  src = BrandLogoDirective.FALLBACK;
  alt = 'orderYaa';

  ngOnChanges(): void {
    this.src = `images/brand/orderyaa-${this.surface}${this.onDark ? '-white' : ''}.png`;
    this.alt = this.surface === 'customer'
      ? 'orderYaa'
      : `orderYaa ${this.surface === 'rider' ? 'Rider' : 'Partner'}`;
  }

  onError(): void {
    // Only ever step down to the fallback, never loop on it.
    if (this.src !== BrandLogoDirective.FALLBACK) this.src = BrandLogoDirective.FALLBACK;
  }
}
